import api from 'services/api';
import { WaitEvent } from 'api/node_instances/operations/getNodeInstanceWaitEvents';
import { isFileEventType } from 'utils/eventTypes';

/**
 * Whether this wait event maps to a known raise endpoint. False for an
 * unresolved flow-child path (null eventType) and for any type outside the
 * file/process buckets.
 */
export const isRaiseable = (event: WaitEvent): boolean => event.eventType !== null && (isFileEventType(event.eventType) || event.eventType === 'processEvent');

/**
 * Raises the event a node instance is waiting on. Keyed on zone + name (the
 * data the wait event already carries), NOT event_id — so it works from the
 * instance viewer, which never loads the events list into the redux store and
 * therefore can't use eventsSlice's raiseProcessEvent/raiseFileEvent (those
 * reject with "Event not found in state").
 *
 * NOTE: the raise endpoints are NOT idempotent — each call inserts a fresh
 * raised event_instances row (no upsert). Callers must gate on `raised` and
 * dedupe by eventId before firing, or the same event accrues multiple raised
 * instances.
 */
export const raiseWaitEvent = async (event: WaitEvent): Promise<void> => {
  if (!isRaiseable(event) || event.eventType === null) {
    throw new Error(`Cannot raise event of type ${event.eventType ?? 'unknown'}.`);
  }
  const zone = encodeURIComponent(event.zoneName);
  const name = encodeURIComponent(event.eventName);
  const endpoint = isFileEventType(event.eventType) ? 'raise_file_event' : 'raise_process_event';
  await api.post(`/events/${zone}/${name}/${endpoint}`);
};
